'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { Allotment } from 'allotment';
import 'allotment/dist/style.css';
import '../../App.css';
import './FullControlEditor.scss';
import { useSite } from '@/lib/site-context';
import { isTauri } from '@/lib/runtime-bridge';
import { generateSketchId } from '@/lib/id-generator';

import {
  TopToolbar,
  CodeEditorPanel,
  ControlsPanel,
  ExecutionStatusRow,
  VisualizationPanel,
  GCodePanel,
  EditorLayout,
  StatusBar,
  AIChat
} from '@/app/components';
import { ControlDefinition } from '@/types/api';
import { AIProvider } from '@/types/ai';

import { useExecutionEngine } from '../hooks/useExecutionEngine';
import { useSketchPersistence } from '../hooks/useSketchPersistence';
import { usePanelLayout } from '../hooks/usePanelLayout';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

interface EditorData {
  id?: string;
  title: string;
  description?: string;
  code: string;
  svgThumbnail?: string;
  isPublic?: boolean;
  createdAt?: string;
  updatedAt?: string;
  views?: number;
  likes?: number;
  tags?: string[];
  controls?: ControlDefinition[];
  author?: {
    id: string;
    name?: string;
    username?: string;
    image?: string;
  };
}

interface FullControlEditorProps {
  mode: 'create' | 'view' | 'example';
  initialData?: EditorData;
  loading?: boolean;
  error?: string | null;
  onDataLoad?: () => Promise<void>;
  showForkButton?: boolean;
  onFork?: () => void;
  canEdit?: boolean;
}

const DEFAULT_PY_CODE = `
layer_height = 0.2
line_width = 0.4
# Define the printer settings to override the default settings (shown below)
printer_name = 'generic'
printer_settings = {
    'primer': 'travel',
    'print_speed': 40*60,  # mm/min
    'travel_speed': 80*60,  # mm/min
    'nozzle_temp': 210,
    'bed_temp': 50,
    'fan_percent': 100,
    'extrusion_width': line_width,
    'extrusion_height': layer_height
}

# Define square parameters
side_length = 20
# Starting position
start_x, start_y = 50, 50

# Create the square
steps.append(fc.Point(x=start_x, y=start_y, z=layer_height))

# Square corners
steps.append(fc.Point(x=start_x + side_length, y=start_y, z=layer_height))
steps.append(fc.Point(x=start_x + side_length, y=start_y + side_length, z=layer_height))
steps.append(fc.Point(x=start_x, y=start_y + side_length, z=layer_height))
steps.append(fc.Point(x=start_x, y=start_y, z=layer_height))

steps.append(fc.Extruder(on=False))  # Turn off extruder
`;
const DEFAULT_JS_CODE = `const layer_height = 0.2;
const line_width = 0.4;
// Printer / extrusion style parameters (can be tweaked)
printer_name = 'generic';
printer_settings = {
  primer: 'travel',
  print_speed: 40*60,
  travel_speed: 80*60,
  nozzle_temp: 210,
  bed_temp: 50,
  fan_percent: 100,
  extrusion_width: line_width,
  extrusion_height: layer_height
};

// Geometry parameters
const side_length = 20;
const start_x = 50;
const start_y = 50;

// Build square path (extruding)
steps.push(new fc.Point({'x':start_x, 'y':start_y, 'z':layer_height}));
steps.push(new fc.Point({'x': start_x + side_length, 'y': start_y, 'z': layer_height}));
steps.push(new fc.Point({'x': start_x + side_length, 'y': start_y + side_length, 'z': layer_height}));
steps.push(new fc.Point({'x': start_x, 'y': start_y + side_length, 'z': layer_height}));
steps.push(new fc.Point({'x': start_x, 'y': start_y, 'z': layer_height}));

// Turn off extruder
steps.push(new fc.Extruder({ on: false }));`;

export default function FullControlEditor({
  mode,
  initialData,
  loading = false,
  error = null,
  onDataLoad,
  canEdit = true,
  showForkButton = false,
  onFork
}: FullControlEditorProps) {
  const site = useSite();

  // Sketch ID management
  const [currentSketchId] = useState(() => initialData?.id || generateSketchId());

  // Editor code state
  const initialDefault = (site.id === 'py' || site.id === 'desktop') ? DEFAULT_PY_CODE : DEFAULT_JS_CODE;
  const [editorCode, setEditorCode] = useState(initialData?.code || initialDefault);

  // Runtime readiness
  const [runtimeReady, setRuntimeReady] = useState(site.id === 'js');

  // Controls state
  const [controls, setControls] = useState<ControlDefinition[]>(initialData?.controls || []);
  const defaultControlValues: Record<string, any> = {};
  controls.forEach(control => {
    if (control.type === 'numeric') {
      defaultControlValues[control.name] = control.default;
    } else if (control.type === 'file') {
      defaultControlValues[control.name] = control.defaultPath || null;
    }
  });
  const [controlValues, setControlValues] = useState<Record<string, any>>(defaultControlValues);

  // AI Assistant state
  const [aiSettings, setAiSettings] = useState<{
    enableAiAssistant: boolean;
    aiProvider: AIProvider;
    aiApiKey: string;
    aiApiEndpoint: string;
    aiModel: string;
  } | null>(null);
  const [showAiChat, setShowAiChat] = useState(false);

  // Initialize from initial data (first mount only)
  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current && initialData) {
      setEditorCode(initialData.code);
      if (initialData.controls && initialData.controls.length > 0) {
        setControls(initialData.controls);
        const loadedValues: Record<string, any> = {};
        initialData.controls.forEach(control => {
          if (control.type === 'numeric') {
            loadedValues[control.name] = control.default;
          } else if (control.type === 'file') {
            loadedValues[control.name] = control.defaultPath || null;
          }
        });
        setControlValues(loadedValues);
      }
      didInit.current = true;
    }
  }, [initialData]);

  // Initialize Pyodide runtime
  useEffect(() => {
    if (site.feature.usePyodide && !isTauri()) {
      const initPyodide = async () => {
        try {
          const { initializePyodide } = await import('@/lib/services/pyodideService');
          await initializePyodide();
          setRuntimeReady(true);
        } catch (error) {
          console.error('Failed to initialize Pyodide:', error);
        }
      };
      initPyodide();
    } else {
      setRuntimeReady(true);
    }
  }, [site]);

  // === Composed Hooks ===

  const [execState, execActions] = useExecutionEngine({
    site,
    editorCode,
    controlValues,
    controls,
    currentSketchId,
    runtimeReady,
    initialData,
  });

  const persistence = useSketchPersistence({
    mode,
    currentSketchId,
    editorCode,
    svgThumbnail: execState.svgThumbnail,
    controls,
    initialData,
  });

  const panels = usePanelLayout();

  useKeyboardShortcuts({
    isExecuting: execState.isExecuting,
    runtimeReady,
    isSaving: persistence.isSaving,
    canEdit,
    isAuthenticated: !!persistence.session || isTauri(),
    executeCode: execActions.executeCode,
    saveSketch: persistence.saveSketch,
  });

  // Fetch AI settings if user is logged in
  useEffect(() => {
    if (persistence.session?.user && canEdit) {
      fetch('/api/settings/ai')
        .then(res => res.json())
        .then(data => {
          if (data.enableAiAssistant && data.aiApiKeyExists) {
            setAiSettings({
              enableAiAssistant: data.enableAiAssistant,
              aiProvider: data.aiProvider,
              aiApiKey: '',
              aiApiEndpoint: data.aiApiEndpoint || '',
              aiModel: data.aiModel || '',
            });
          }
        })
        .catch(error => {
          console.error('Failed to fetch AI settings:', error);
        });
    }
  }, [persistence.session, canEdit]);

  const handleEditorChange = useCallback((value: string | undefined) => {
    if (canEdit) {
      setEditorCode(value || '');
    }
  }, [canEdit]);

  // Prepare toolbar props based on mode
  const getToolbarProps = () => {
    const baseProps = {
      sketchTitle: persistence.sketchTitle,
      isAuthenticated: !!persistence.session || isTauri(),
      isSaving: persistence.isSaving,
      svgThumbnail: execState.svgThumbnail,
      hasControls: controls.length > 0,
      sketchId: currentSketchId,
    };

    switch (mode) {
      case 'create':
        return {
          ...baseProps,
          onTitleChange: persistence.setSketchTitle,
          onSave: (persistence.session || isTauri()) ? persistence.saveSketch : undefined,
          lastSaved: persistence.lastSaved,
        };
      case 'view':
        return {
          ...baseProps,
          isViewOnly: !canEdit,
          onTitleChange: canEdit ? persistence.setSketchTitle : undefined,
          onSave: canEdit && (persistence.session || isTauri()) ? persistence.saveSketch : undefined,
          lastSaved: canEdit ? persistence.lastSaved : undefined,
          showForkButton,
          onFork,
          sketchMeta: initialData?.author ? {
            author: initialData.author.username || 'Anonymous',
            authorUsername: initialData.author.username,
            views: initialData.views || 0,
            createdAt: initialData.createdAt || '',
            description: initialData.description
          } : undefined,
        };
      case 'example':
        return {
          ...baseProps,
          onTitleChange: persistence.setSketchTitle,
          onSave: undefined,
          isExample: true,
          onFork,
          showForkButton: true,
        };
      default:
        return baseProps;
    }
  };

  // Debounced sketchTitle for GCodePanel
  const [debouncedTitle, setDebouncedTitle] = useState(persistence.sketchTitle);
  const titleDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (titleDebounceTimer.current) clearTimeout(titleDebounceTimer.current);
    titleDebounceTimer.current = setTimeout(() => {
      setDebouncedTitle(persistence.sketchTitle);
    }, 400);
    return () => {
      if (titleDebounceTimer.current) clearTimeout(titleDebounceTimer.current);
    };
  }, [persistence.sketchTitle]);

  // === Render ===

  if (loading) {
    return (
      <EditorLayout>
        <div className="app-container">
          <div className="loading-page">
            <div>Loading...</div>
          </div>
        </div>
      </EditorLayout>
    );
  }

  if (error) {
    return (
      <EditorLayout>
        <div className="app-container">
          <div className="error-page">
            <h2>Error</h2>
            <p>{error}</p>
          </div>
        </div>
      </EditorLayout>
    );
  }

  return (
    <EditorLayout>
      <div className="app-container">
        <TopToolbar {...getToolbarProps()} />

        <div className="main-content">
          <Allotment defaultSizes={[50, 50]} onChange={() => { }}>
            {/* Left Side: Code Editor + Controls */}
            <Allotment.Pane minSize={200}>
              <Allotment vertical ref={panels.leftAllotmentRef} defaultSizes={panels.leftPanelSizes} onChange={panels.setLeftPanelSizes} snap>
                {/* Code Editor Panel */}
                <Allotment.Pane minSize={40}>
                  <CodeEditorPanel
                    code={editorCode}
                    onChange={handleEditorChange}
                    onExecute={execActions.executeCode}
                    isExecuting={execState.isExecuting}
                    autoCompile={execState.autoCompile}
                    onAutoCompileChange={execActions.setAutoCompile}
                  />
                </Allotment.Pane>

                {/* Controls Panel */}
                <Allotment.Pane minSize={40}>
                  {/* Execution Status Row */}
                  <ExecutionStatusRow
                    status={execState.executionStatus}
                    isExecuting={execState.isExecuting}
                    lastExecutionTime={execState.lastExecutionTime}
                    avgExecutionTime={execState.avgExecutionTime}
                    fcLoaded={site.id === 'js' ? execState.fcLoaded : undefined}
                    stepsCount={site.id === 'js' ? execState.stepsCount : undefined}
                  />
                  <ControlsPanel
                    controls={controls}
                    onControlsChange={canEdit ? setControls : undefined}
                    controlValues={controlValues}
                    onControlValuesChange={setControlValues}
                    canEdit={canEdit}
                    isExecuting={execState.isExecuting}
                    lastExecutionTime={execState.lastExecutionTime}
                    avgExecutionTime={execState.avgExecutionTime}
                    autoUpdate={execState.autoUpdate}
                    onAutoUpdateChange={execActions.setAutoUpdate}
                    description={persistence.sketchDescription}
                    onDescriptionChange={canEdit ? persistence.setSketchDescription : undefined}
                    sketchId={initialData?.id}
                    showSocialFeatures={!isTauri() && mode === 'view' && !!initialData?.id}
                    aiEnabled={!!aiSettings}
                    onAiClick={aiSettings ? () => setShowAiChat(!showAiChat) : undefined}
                  />
                </Allotment.Pane>
              </Allotment>
            </Allotment.Pane>

            {/* Right Side: Visualization + G-Code */}
            <Allotment.Pane minSize={200}>
              <Allotment vertical ref={panels.rightAllotmentRef} defaultSizes={panels.rightPanelSizes} onChange={panels.setRightPanelSizes} snap>
                {/* Visualization Panel */}
                <Allotment.Pane minSize={40}>
                  <VisualizationPanel
                    plotData={execState.plotData}
                    plotLayout={execState.plotLayout}
                    svgThumbnail={execState.svgThumbnail}
                  />
                </Allotment.Pane>

                {/* G-Code Panel */}
                <Allotment.Pane minSize={40}>
                  <GCodePanel
                    gcode={execState.gcode}
                    steps={execState.steps}
                    controlValues={controlValues}
                    title={debouncedTitle}
                  />
                </Allotment.Pane>
              </Allotment>
            </Allotment.Pane>
          </Allotment>

          {/* AI Chat Component - conditionally rendered */}
          {showAiChat && aiSettings && (
            <div className="ai-chat-container">
              <AIChat
                aiProvider={aiSettings.aiProvider}
                aiApiKey={aiSettings.aiApiKey}
                aiApiEndpoint={aiSettings.aiApiEndpoint}
                aiModel={aiSettings.aiModel}
                sketchCode={editorCode}
                sketchErrors={execState.executionErrors}
                controls={controls}
                siteVariant={site.id}
                onCodeUpdate={(code: string, explanation: string) => {
                  setEditorCode(code);
                  execActions.setExecutionStatus(`AI updated code: ${explanation}`);
                  if (runtimeReady) {
                    setTimeout(() => execActions.executeCode(true), 500);
                  }
                }}
                onControlsUpdate={(newControls: ControlDefinition[]) => {
                  setControls(newControls);
                  execActions.setExecutionStatus('AI updated controls');
                }}
                onClose={() => setShowAiChat(false)}
              />
            </div>
          )}
        </div>

        <StatusBar
          sketchTitle={persistence.sketchTitle}
          canEdit={canEdit}
          lineCount={editorCode.split('\n').length}
          lastSaved={persistence.lastSaved || undefined}
        />
      </div>
    </EditorLayout>
  );
}
